import { useState } from 'react'
import { Check, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useSettings } from '@/context/settings-hooks'
import { contrastRatio, hexToHsl } from '../color'
import { BUILTIN_BASE_ROLES } from '../theme-model'
import type { BaseRoles, CustomTheme } from '../theme-model'
import { CUSTOM_THEMES_ENABLED, EDGE_THEMES, STARTER_PRESETS } from '../theme-presets'
import { customThemeToOption, type ThemeOption } from '../theme-runtime'
import { TEXT_SCALES, TEXT_SCALE_LABELS } from '../text-scale'

const HEX_RE = /^#[0-9a-fA-F]{6}$/
/** 4.5:1 is the WCAG AA body-text bar; below it we warn. */
const MIN_CONTRAST = 4.5

const ROLE_ROWS: { key: keyof BaseRoles; label: string }[] = [
  { key: 'background', label: 'Background' },
  { key: 'foreground', label: 'Text' },
  { key: 'accent', label: 'Accent' },
  { key: 'rise', label: 'Rise' },
  { key: 'fall', label: 'Fall' },
  { key: 'warning', label: 'Warning' },
]

function safeContrast(a: string, b: string): number | null {
  try {
    return contrastRatio(hexToHsl(a), hexToHsl(b))
  } catch {
    return null
  }
}

/** A single always-editable role: color swatch + hex field. Partial hex typing
 *  is buffered locally and only committed once it's a valid `#rrggbb`. */
function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (hex: string) => void
}) {
  const [text, setText] = useState(value)
  const [synced, setSynced] = useState(value)
  if (value !== synced) {
    setSynced(value)
    setText(value)
  }
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        aria-label={`${label} color`}
        value={HEX_RE.test(value) ? value : '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-8 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0"
      />
      <div className="flex flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
        <Input
          aria-label={`${label} hex`}
          value={text}
          onChange={(e) => {
            const next = e.target.value
            setText(next)
            if (HEX_RE.test(next)) onChange(next)
          }}
          className="h-6 w-24 font-mono text-xs"
        />
      </div>
    </div>
  )
}

function newDraft(): CustomTheme {
  return {
    id: crypto.randomUUID(),
    name: 'My theme',
    baseMode: 'dark',
    roles: { ...BUILTIN_BASE_ROLES.dark },
  }
}

export function AppearanceDialog({
  open,
  onOpenChange,
  allowCustom = CUSTOM_THEMES_ENABLED,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Show the custom-theme editor (color pickers, New theme, rename/delete).
   *  Defaults to the ship flag; tests pass `true` to exercise the editor. */
  allowCustom?: boolean
}) {
  const {
    themeId,
    selectTheme,
    saveCustomTheme,
    deleteCustomTheme,
    customThemes,
    textScale,
    setTextScale,
  } = useSettings()
  const customOptions = customThemes.map(customThemeToOption)
  const allOptions = [...EDGE_THEMES, ...STARTER_PRESETS, ...customOptions]

  const activeOption = allOptions.find((o) => o.id === themeId) ?? allOptions[0]
  const activeCustom = customThemes.find((t) => t.id === themeId) ?? null

  // Editing a role. A custom theme updates in place; a read-only preset/built-in
  // forks a new custom theme (a copy carrying the change) and switches to it.
  const editRole = (key: keyof BaseRoles, hex: string) => {
    if (!HEX_RE.test(hex)) return
    if (hex.toLowerCase() === (activeOption.roles[key] ?? '').toLowerCase()) return
    if (activeCustom) {
      saveCustomTheme({ ...activeCustom, roles: { ...activeCustom.roles, [key]: hex } })
    } else {
      saveCustomTheme({
        id: crypto.randomUUID(),
        name: `${activeOption.name} copy`,
        baseMode: activeOption.baseMode,
        roles: { ...activeOption.roles, [key]: hex },
      })
    }
  }

  const contrast = safeContrast(activeOption.roles.foreground, activeOption.roles.background)
  const lowContrast = contrast !== null && contrast < MIN_CONTRAST

  const row = (o: ThemeOption) => (
    <button
      key={o.id}
      type="button"
      aria-pressed={o.id === themeId}
      onClick={() => selectTheme(o.id)}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
        o.id === themeId ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-hover',
      )}
    >
      <span
        className="h-3 w-3 shrink-0 rounded-full border border-border"
        style={{ background: o.roles.background }}
      />
      <span className="flex-1 truncate">{o.name}</span>
      {o.id === themeId && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
    </button>
  )

  const group = (title: string, options: ThemeOption[]) =>
    options.length > 0 ? (
      <div className="space-y-0.5">
        <h3 className="px-2 pb-0.5 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        {options.map(row)}
      </div>
    ) : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideOverlay
        className={cn(
          'flex h-[434px] max-h-[88vh] flex-col gap-0 overflow-hidden p-0',
          allowCustom ? 'max-w-3xl' : 'max-w-sm',
        )}
      >
        {/* Header — title + a full-width divider; the close X is provided by DialogContent */}
        <div className="flex shrink-0 items-center border-b border-border px-5 py-3.5">
          <DialogTitle>Appearance</DialogTitle>
        </div>
        <DialogDescription className="sr-only">
          Pick a text size and a theme, and edit its colors. Editing a preset saves a copy.
        </DialogDescription>

        {/* Body — presets-only ships as a single options column; the gated
            custom editor adds a second column of color pickers (no preview). */}
        <div
          className={cn(
            'grid min-h-0 flex-1',
            allowCustom ? 'grid-cols-[200px_1fr]' : 'grid-cols-1',
          )}
        >
          {/* Options rail — the theme/color choices. A recessed surface so each
              theme's swatch reads clearly. `background` rather than a literal
              black overlay: it is the step below popover in every theme, where
              bg-black/20 was tuned for a dark dialog and dropped a heavy grey
              slab over the white one. */}
          <div
            className={cn(
              'flex min-h-0 flex-col bg-background',
              allowCustom && 'border-r border-border',
            )}
          >
            {allowCustom && (
              <div className="shrink-0 border-b border-border p-2">
                <Button
                  variant="ghost"
                  className="w-full justify-start px-2 text-primary hover:text-primary"
                  onClick={() => saveCustomTheme(newDraft())}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New theme
                </Button>
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
              {/* Text size: scales text only, never spacing (see text-scale.ts). */}
              <div className="space-y-0.5">
                <h3 className="px-2 pb-0.5 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Text size
                </h3>
                <div role="group" aria-label="Text size" className="grid grid-cols-3 gap-1 px-1">
                  {TEXT_SCALES.map((scale) => (
                    <button
                      key={scale}
                      type="button"
                      aria-pressed={scale === textScale}
                      aria-label={`${TEXT_SCALE_LABELS[scale]} ${scale}%`}
                      onClick={() => setTextScale(scale)}
                      className={cn(
                        'flex flex-col items-center rounded-md px-1 py-1.5 text-xs transition-colors',
                        scale === textScale
                          ? 'bg-accent text-accent-foreground'
                          : 'text-foreground hover:bg-hover',
                      )}
                    >
                      <span>{TEXT_SCALE_LABELS[scale]}</span>
                      <span className="text-[10px] tabular-nums text-muted-foreground">
                        {scale}%
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              {group('Edge', EDGE_THEMES)}
              {group('Presets', STARTER_PRESETS)}
              {group('My themes', customOptions)}
            </div>
          </div>

          {/* Editor pane — only when the custom editor is enabled. No preview. */}
          {allowCustom && (
            <div className="min-h-0 overflow-y-auto p-5">
              <div className="flex flex-col gap-5">
                <div className="flex items-center gap-2">
                  {activeCustom ? (
                    <Input
                      aria-label="Name"
                      value={activeCustom.name}
                      onChange={(e) => saveCustomTheme({ ...activeCustom, name: e.target.value })}
                      className="max-w-[220px] font-semibold"
                    />
                  ) : (
                    <h2 className="text-base font-semibold text-foreground">{activeOption.name}</h2>
                  )}
                  {activeCustom && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete theme"
                      className="text-destructive hover:text-destructive"
                      onClick={() => deleteCustomTheme(themeId)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                  {ROLE_ROWS.map(({ key, label }) => (
                    <ColorRow
                      key={key}
                      label={label}
                      value={activeOption.roles[key] ?? '#000000'}
                      onChange={(hex) => editRole(key, hex)}
                    />
                  ))}
                </div>
                {lowContrast && contrast !== null && (
                  <p className="text-xs text-amber-500">
                    Low contrast: text may be hard to read ({contrast.toFixed(1)}:1)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
